import { useEffect, useState } from "react";
import { API, authFetch } from "@/App";
import { toast } from "sonner";
import {
  UserCheck, MapPin, Clock, Camera, Navigation, Battery,
  CheckCircle2, AlertTriangle, Users, Eye, RefreshCw, Circle,
  Calendar as CalendarIcon, Phone, LogIn, LogOut, Activity,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const StatChip = ({ icon: Icon, label, value, color = "slate", testId }) => (
  <Card className={`p-3 border-l-4 border-l-${color}-500`} data-testid={testId}>
    <div className="flex items-center gap-2">
      <Icon className={`w-4 h-4 text-${color}-500`} />
      <div>
        <p className="text-[10px] uppercase text-slate-500 tracking-wide">{label}</p>
        <p className={`text-xl font-bold font-mono text-${color}-700`}>{value}</p>
      </div>
    </div>
  </Card>
);

const PhotoThumb = ({ src, alt, onClick, testId }) => {
  if (!src) return (
    <div className="w-12 h-12 bg-slate-100 rounded flex items-center justify-center" data-testid={`${testId}-empty`}>
      <Camera className="w-4 h-4 text-slate-300" />
    </div>
  );
  const url = src.startsWith("http") ? src : `${API}/files/${src}?auth=${localStorage.getItem("session_token")}`;
  return (
    <button type="button" onClick={() => onClick(url, alt)} className="w-12 h-12 rounded overflow-hidden border hover:border-[#8B1A1A] transition" data-testid={testId}>
      <img src={url} alt={alt} className="w-full h-full object-cover" />
    </button>
  );
};

const FieldCheckInsPage = () => {
  const today = new Date().toISOString().split("T")[0];
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(today);
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [photo, setPhoto] = useState(null);
  const [detailEmployee, setDetailEmployee] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ date });
      if (status !== "all") qs.append("status", status);
      const res = await authFetch(`${API}/field-tracking/check-ins?${qs}`);
      const d = await res.json();
      if (d.success) {
        setData(d.data || []);
        setSummary(d.meta?.summary || {});
      }
    } catch { toast.error("Failed to load check-ins"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [date, status, refreshKey]);

  // Auto-refresh every 30 seconds for live view (today only)
  useEffect(() => {
    if (!autoRefresh || date !== today) return;
    const interval = setInterval(() => setRefreshKey(k => k + 1), 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, date, today]);

  const filtered = data.filter(r => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (r.employee_name || "").toLowerCase().includes(q) ||
      (r.employee_code || "").toLowerCase().includes(q) ||
      (r.mobile || "").includes(q);
  });

  const openMap = (loc) => {
    if (!loc?.lat || !loc?.lng) return toast.error("No GPS available");
    window.open(`https://www.google.com/maps?q=${loc.lat},${loc.lng}`, "_blank");
  };

  return (
    <div className="space-y-6" data-testid="field-checkins-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Sora'] flex items-center gap-2">
            <UserCheck className="w-6 h-6 text-[#8B1A1A]" /> Field Punch-In / Punch-Out
          </h1>
          <p className="text-sm text-slate-500 mt-1">Live monitor of employee <strong>punch-in / punch-out</strong> (daily attendance) with selfie, GPS and on-field activity. For dealer visits, see the Dealer Visits section.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setAutoRefresh(!autoRefresh)}
            className={autoRefresh ? "border-emerald-300 text-emerald-700" : ""}
            data-testid="auto-refresh-toggle">
            <Activity className={`w-3.5 h-3.5 mr-1 ${autoRefresh ? "text-emerald-500" : ""}`} />
            Auto {autoRefresh ? "ON" : "OFF"}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setRefreshKey(k => k + 1)} data-testid="refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Summary Chips */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
        <StatChip icon={Users} label="Total" value={summary.total || 0} color="slate" testId="stat-total" />
        <StatChip icon={Circle} label="Online Now" value={summary.online || 0} color="emerald" testId="stat-online" />
        <StatChip icon={AlertTriangle} label="Late" value={summary.late || 0} color="amber" testId="stat-late" />
        <StatChip icon={LogOut} label="Not Punched Out" value={summary.not_checked_out || 0} color="red" testId="stat-not-out" />
        <StatChip icon={Camera} label="With Selfie" value={summary.with_selfie || 0} color="blue" testId="stat-selfie" />
        <StatChip icon={Clock} label="Avg Hours" value={summary.avg_hours || 0} color="purple" testId="stat-hours" />
      </div>

      {/* Filters */}
      <Card className="p-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2">
            <CalendarIcon className="w-4 h-4 text-slate-400" />
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-44" data-testid="date-picker" max={today} />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-44" data-testid="status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Employees</SelectItem>
              <SelectItem value="online">Online Now</SelectItem>
              <SelectItem value="offline">Offline</SelectItem>
              <SelectItem value="late">Late Arrivals</SelectItem>
              <SelectItem value="not_checked_out">Not Punched Out</SelectItem>
            </SelectContent>
          </Select>
          <Input placeholder="Search name / code / mobile" value={search} onChange={(e) => setSearch(e.target.value)} className="flex-1 min-w-[200px]" data-testid="search-checkins" />
          <span className="text-xs text-slate-400">{filtered.length} rows</span>
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table text-sm" data-testid="checkins-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Live</th>
                <th>Punch-In</th>
                <th>Selfie</th>
                <th>Punch-Out</th>
                <th>Selfie</th>
                <th className="text-right">Hours</th>
                <th className="text-center">Activity</th>
                <th>Location</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="text-center py-8"><div className="w-6 h-6 spinner mx-auto" /></td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={10} className="text-center py-12 text-slate-400">
                  <UserCheck className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  No punch-in records found for this filter
                </td></tr>
              ) : filtered.map((r) => (
                <tr key={r.attendance_id || r.employee_id} data-testid={`checkin-row-${r.employee_id}`} className={r.is_late ? "bg-amber-50/40" : ""}>
                  <td>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-[#8B1A1A]/10 flex items-center justify-center shrink-0">
                        <span className="text-[#8B1A1A] font-medium text-xs">{r.employee_name?.charAt(0) || "?"}</span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 text-sm">{r.employee_name}</p>
                        <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
                          <span>{r.employee_code}</span>
                          {r.mobile && <><span>·</span><Phone className="w-2.5 h-2.5" />{r.mobile}</>}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    {r.is_online ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700" data-testid={`online-${r.employee_id}`}>
                        <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" /> LIVE
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] text-slate-400">
                        <Circle className="w-2 h-2" /> {r.last_seen ? `last ${new Date(r.last_seen).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}` : "offline"}
                      </span>
                    )}
                    {r.last_battery != null && (
                      <div className="flex items-center gap-0.5 text-[10px] text-slate-500 mt-0.5">
                        <Battery className={`w-2.5 h-2.5 ${r.last_battery < 20 ? "text-red-500" : "text-slate-400"}`} />
                        {r.last_battery}%
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="font-mono text-sm">{r.check_in_time || "-"}</div>
                    {r.is_late && <Badge className="text-[9px] bg-amber-100 text-amber-700 border-amber-200 mt-0.5">Late</Badge>}
                  </td>
                  <td>
                    <PhotoThumb src={r.check_in_selfie} alt={`${r.employee_name} punch-in`} onClick={(u, a) => setPhoto({ url: u, alt: a })} testId={`checkin-selfie-${r.employee_id}`} />
                  </td>
                  <td>
                    <div className="font-mono text-sm">{r.check_out_time || "-"}</div>
                    {r.check_in_time && !r.check_out_time && <Badge className="text-[9px] bg-red-100 text-red-700 border-red-200 mt-0.5">Pending</Badge>}
                  </td>
                  <td>
                    <PhotoThumb src={r.check_out_selfie} alt={`${r.employee_name} punch-out`} onClick={(u, a) => setPhoto({ url: u, alt: a })} testId={`checkout-selfie-${r.employee_id}`} />
                  </td>
                  <td className="text-right font-mono">{(r.hours_on_field || 0).toFixed(1)}h</td>
                  <td className="text-center">
                    <div className="flex items-center gap-2 justify-center text-[10px]">
                      <span title="Visits" className="flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5 text-blue-500" />{r.visit_count || 0}</span>
                      <span title="Orders" className="flex items-center gap-0.5"><LogIn className="w-2.5 h-2.5 text-emerald-500" />{r.orders_count || 0}</span>
                      <span title="Collections" className="flex items-center gap-0.5"><CheckCircle2 className="w-2.5 h-2.5 text-purple-500" />{r.collections_count || 0}</span>
                    </div>
                  </td>
                  <td>
                    {r.check_in_location ? (
                      <button type="button" onClick={() => openMap(r.check_in_location)} className="text-xs text-blue-600 hover:underline flex items-center gap-1" data-testid={`gps-${r.employee_id}`}>
                        <Navigation className="w-3 h-3" /> View
                      </button>
                    ) : <span className="text-xs text-slate-300">-</span>}
                  </td>
                  <td>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setDetailEmployee(r)} data-testid={`detail-${r.employee_id}`}>
                      <Eye className="w-3 h-3 mr-1" /> Details
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Photo Lightbox */}
      <Dialog open={!!photo} onOpenChange={(o) => { if (!o) setPhoto(null); }}>
        <DialogContent className="max-w-2xl p-2" data-testid="photo-lightbox">
          <DialogHeader className="px-2 pt-2">
            <DialogTitle>{photo?.alt}</DialogTitle>
            <DialogDescription>Selfie uploaded during field punch-in/punch-out.</DialogDescription>
          </DialogHeader>
          {photo && <img src={photo.url} alt={photo.alt} className="w-full h-auto rounded" />}
        </DialogContent>
      </Dialog>

      {/* Employee Details Drawer */}
      <Dialog open={!!detailEmployee} onOpenChange={(o) => { if (!o) setDetailEmployee(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="checkin-detail">
          {detailEmployee && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <UserCheck className="w-5 h-5 text-[#8B1A1A]" /> {detailEmployee.employee_name}
                  {detailEmployee.is_online && <Badge className="ml-2 bg-emerald-100 text-emerald-700 text-[10px]">LIVE</Badge>}
                </DialogTitle>
                <DialogDescription>{detailEmployee.employee_code} · {detailEmployee.role} · {detailEmployee.mobile}</DialogDescription>
              </DialogHeader>

              <div className="grid grid-cols-2 gap-4 mt-2">
                <Card className="p-4">
                  <h3 className="font-semibold text-sm text-slate-800 flex items-center gap-1.5 mb-3">
                    <LogIn className="w-4 h-4 text-emerald-500" /> Punch-In
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Time</span>
                      <span className="font-mono font-semibold">{detailEmployee.check_in_time || "-"}</span>
                    </div>
                    {detailEmployee.check_in_location && (
                      <button type="button" onClick={() => openMap(detailEmployee.check_in_location)} className="w-full flex justify-between items-center hover:bg-slate-50 rounded p-1 -m-1">
                        <span className="text-slate-500">GPS</span>
                        <span className="text-blue-600 text-xs flex items-center gap-1"><Navigation className="w-3 h-3" /> Map</span>
                      </button>
                    )}
                    {detailEmployee.is_late && <Badge className="bg-amber-100 text-amber-700 text-[10px]">Late arrival</Badge>}
                    {detailEmployee.check_in_selfie && (
                      <div className="mt-3 aspect-square rounded overflow-hidden">
                        <img src={`${API}/files/${detailEmployee.check_in_selfie}?auth=${localStorage.getItem("session_token")}`} alt="check-in" className="w-full h-full object-cover" />
                      </div>
                    )}
                  </div>
                </Card>
                <Card className="p-4">
                  <h3 className="font-semibold text-sm text-slate-800 flex items-center gap-1.5 mb-3">
                    <LogOut className="w-4 h-4 text-red-500" /> Punch-Out
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Time</span>
                      <span className="font-mono font-semibold">{detailEmployee.check_out_time || "Not yet"}</span>
                    </div>
                    {detailEmployee.check_out_location && (
                      <button type="button" onClick={() => openMap(detailEmployee.check_out_location)} className="w-full flex justify-between items-center hover:bg-slate-50 rounded p-1 -m-1">
                        <span className="text-slate-500">GPS</span>
                        <span className="text-blue-600 text-xs flex items-center gap-1"><Navigation className="w-3 h-3" /> Map</span>
                      </button>
                    )}
                    <div className="flex justify-between">
                      <span className="text-slate-500">Hours</span>
                      <span className="font-mono font-semibold">{(detailEmployee.hours_on_field || 0).toFixed(1)}h</span>
                    </div>
                    {detailEmployee.check_out_selfie && (
                      <div className="mt-3 aspect-square rounded overflow-hidden">
                        <img src={`${API}/files/${detailEmployee.check_out_selfie}?auth=${localStorage.getItem("session_token")}`} alt="check-out" className="w-full h-full object-cover" />
                      </div>
                    )}
                  </div>
                </Card>
              </div>

              {/* Day activity */}
              <Card className="p-4 mt-4">
                <h3 className="font-semibold text-sm text-slate-800 flex items-center gap-1.5 mb-3">
                  <Activity className="w-4 h-4 text-purple-500" /> Today's Field Activity
                </h3>
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center p-2 bg-blue-50 rounded">
                    <p className="text-2xl font-mono font-bold text-blue-600">{detailEmployee.visit_count || 0}</p>
                    <p className="text-[10px] uppercase text-blue-500">Dealer Visits</p>
                  </div>
                  <div className="text-center p-2 bg-emerald-50 rounded">
                    <p className="text-2xl font-mono font-bold text-emerald-600">{detailEmployee.orders_count || 0}</p>
                    <p className="text-[10px] uppercase text-emerald-500">Orders</p>
                  </div>
                  <div className="text-center p-2 bg-purple-50 rounded">
                    <p className="text-2xl font-mono font-bold text-purple-600">{detailEmployee.collections_count || 0}</p>
                    <p className="text-[10px] uppercase text-purple-500">Collections</p>
                  </div>
                </div>
              </Card>

              {detailEmployee.last_battery != null && (
                <Card className="p-3 mt-4 flex items-center gap-3">
                  <Battery className={`w-5 h-5 ${detailEmployee.last_battery < 20 ? "text-red-500" : "text-emerald-500"}`} />
                  <span className="text-sm">Last known battery: <strong>{detailEmployee.last_battery}%</strong></span>
                  {detailEmployee.last_battery < 20 && <Badge className="bg-red-100 text-red-700 text-[10px] ml-auto">Low Battery</Badge>}
                </Card>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FieldCheckInsPage;
